import clsx from 'clsx';
import { ComponentPropsWithRef } from 'react';
import { IconButton } from '../button/icon-button';
import { InfoCircleIcon, XmarkIcon } from '../icons/react';
import {
  alert,
  alertActions,
  alertBody,
  alertClose,
  alertElevated,
  alertIcon,
  alertSize,
  alertVariant,
} from './alert.css';

const getGridStyles = ({
  size,
  hasActions,
  hasClose,
}: {
  size: 'small' | 'medium';
  hasActions: boolean;
  hasClose: boolean;
}) => {
  if (size === 'small') {
    return {
      gridTemplateColumns: `auto 1fr ${hasActions ? 'auto' : ''} ${hasClose ? 'auto' : ''}`,
      gridTemplateAreas: `
      "icon body ${hasActions ? 'actions' : ''} ${hasClose ? 'close' : ''}"
    `,
    };
  }
  return {
    gridTemplateColumns: `auto 1fr ${hasClose ? 'auto' : ''}`,
    gridTemplateAreas: `
    "icon body ${hasClose ? 'close' : ''}"
    ${hasActions ? `"actions actions ${hasClose ? 'actions' : ''}"` : ''}
  `,
  };
};

export const Alert = ({
  icon,
  children,
  primaryAction,
  secondaryAction,
  color = 'default',
  size = 'small',
  className,
  onClose,
  elevated = false,
  style,
  ...props
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  color?: 'default' | 'prominent';
  size?: 'small' | 'medium';
  className?: string;
  elevated?: boolean;
  onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
} & ComponentPropsWithRef<'div'>) => {
  const hasActions = !!primaryAction || !!secondaryAction;
  const hasClose = !!onClose;

  const gridStyles = getGridStyles({
    size,
    hasActions,
    hasClose,
  });

  return (
    <div
      className={clsx(
        alert,
        alertVariant[color],
        alertSize[size],
        elevated && alertElevated,
        className,
      )}
      style={{ ...style, ...gridStyles }}
      {...props}
    >
      <div className={alertIcon}>{icon || <InfoCircleIcon />}</div>
      <div className={alertBody}>{children}</div>
      {hasActions ? (
        <div className={alertActions}>
          {secondaryAction}
          {primaryAction}
        </div>
      ) : null}
      {onClose && (
        <div className={alertClose}>
          <IconButton
            style={{
              width: '24px',
              height: '24px',
            }}
            size="small"
            variant="ghost"
            onClick={onClose}
          >
            <XmarkIcon />
          </IconButton>
        </div>
      )}
    </div>
  );
};
